import { Redo2, Check, X, Phone, MapPin, FileText, Video, Link as LinkIcon } from 'lucide-react'
import { useDragScroll } from '@/hooks/useDragScroll'
import { pushTargets as targetsFor, type PushTarget } from '@/lib/wall/wallDates'
import { readCadenceConfig } from '@/lib/cadence/config'
import { WALL } from './wallTheme'
import type { WallV2TimelineEvent } from './types'

/**
 * Push reschedules a task from the wall: into a named week or month, onto a
 * weekend's Saturday (all day), or to Someday. Each button shows the dates it
 * means (wallDates.pushTargets). Finer scheduling lives on mobile / desktop.
 */
export type PushPreset =
  | 'this-week'
  | 'this-weekend'
  | 'next-week'
  | 'next-weekend'
  | 'next-month'
  | 'someday'

interface Props {
  event: WallV2TimelineEvent
  /** (id, kind) — id keeps its prefix; the shell strips it for the entity call. */
  onSkip: (id: string, kind: 'event' | 'routine') => void
  /** Now widened to accept tasks; the Shell internally routes task completes
   *  through the same handleToggleComplete the row's checkbox already uses. */
  onMarkDone: (id: string, kind: 'event' | 'routine' | 'task') => void
  /** Task variant only — emits a fuzzy push preset; the Shell maps it to a
   *  bucket mutation. Routines / events never fire this. */
  onPushTask: (id: string, preset: PushPreset) => void
  /** Where each preset lands, for the date line under its button. Defaults to
   *  now and this browser's week start. */
  pushTargets?: Record<PushPreset, PushTarget>
  onClose: () => void
}

// Ordered by horizon so the 2×2-ish grid reads near→far top-to-bottom.
const PUSH_PRESETS: ReadonlyArray<{ preset: PushPreset; label: string }> = [
  { preset: 'this-week',    label: 'This week'    },
  { preset: 'this-weekend', label: 'This weekend' },
  { preset: 'next-week',    label: 'Next week'    },
  { preset: 'next-weekend', label: 'Next weekend' },
  { preset: 'next-month',   label: 'Next month'   },
  { preset: 'someday',      label: 'Someday'      },
]

// Destination-only Maps link — the wall is stationary, so we surface "where is
// this" rather than a turn-by-turn route. Rendered into an <a> (never
// window.open) so the OS hands the universal link to Maps cleanly.
function mapsUrlFor(location: string, placeId?: string): string {
  const base = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
  return placeId ? `${base}&query_place_id=${encodeURIComponent(placeId)}` : base
}

// Shared styling for a tappable context row (phone / location / link / meeting).
const ctxRow =
  `flex items-center gap-3 w-full min-h-[60px] px-4 ${WALL.cardInset} ` +
  `${WALL.inkStrong} active:scale-[0.98] transition-transform`

export function WallV2ItemActionSheet({ event, onSkip, onMarkDone, onPushTask, pushTargets, onClose }: Props) {
  const targets = pushTargets ?? targetsFor(new Date(), readCadenceConfig().weekStartsOn)
  const kind: 'routine' | 'event' | 'task' = event.kind ?? 'event'

  // The Pi delivers touch as mouse events, so the touch-action CSS below never
  // scrolls on the wall. Drive scroll by pointer-drag like the other surfaces.
  const ctxScrollRef = useDragScroll<HTMLDivElement>()

  const hasContext = Boolean(
    event.phoneNumber ||
    event.meetingUrl ||
    event.location ||
    event.notes ||
    (event.links && event.links.length > 0),
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className={`w-[min(92vw,560px)] max-h-[88vh] flex flex-col ${WALL.card}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center shrink-0">
          <div className={`text-[1.4rem] font-bold ${WALL.inkStrong}`}>{event.title}</div>
          {event.subtitle && <div className={`${WALL.muted} mt-1`}>{event.subtitle}</div>}
        </div>

        {/* Context — the rich detail that was planned earlier, surfaced now.
            Scrollable so long notes never push the actions off-screen. */}
        {hasContext && (
          <div
            ref={ctxScrollRef}
            className="px-6 flex-1 min-h-0 overflow-y-auto"
            style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}
          >
            <div className="flex flex-col gap-2.5 pb-1">
              {event.phoneNumber && (
                <a href={`tel:${event.phoneNumber}`} className={ctxRow}>
                  <Phone className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span className="font-bold truncate">{event.phoneNumber}</span>
                  <span className={`ml-auto text-sm shrink-0 ${WALL.muted}`}>Tap to call</span>
                </a>
              )}

              {event.meetingUrl && (
                <a href={event.meetingUrl} target="_blank" rel="noopener noreferrer" className={ctxRow}>
                  <Video className="w-5 h-5 text-sky-600 dark:text-sky-400 shrink-0" />
                  <span className="font-bold truncate">Join meeting</span>
                </a>
              )}

              {event.location && (
                <a
                  href={mapsUrlFor(event.location, event.locationPlaceId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ctxRow}
                >
                  <MapPin className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
                  <span className="font-bold truncate">{event.location}</span>
                  <span className={`ml-auto text-sm shrink-0 ${WALL.muted}`}>Directions</span>
                </a>
              )}

              {event.links?.map((link, i) => (
                <a
                  key={`${link.url}-${i}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ctxRow}
                >
                  <LinkIcon className="w-5 h-5 text-violet-500 dark:text-violet-300 shrink-0" />
                  <span className="font-bold truncate">{link.title || link.url}</span>
                </a>
              ))}

              {event.notes && (
                <div className={`px-4 py-3 ${WALL.cardInset}`}>
                  <div className={`flex items-center gap-2 mb-1.5 ${WALL.label}`}>
                    <FileText className="w-4 h-4" /> Notes
                  </div>
                  <div className={`whitespace-pre-wrap leading-snug ${WALL.inkStrong}`}>
                    {event.notes}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-6 pt-4 pb-6 flex flex-col gap-3 shrink-0">
          {kind === 'task' ? (
            <>
              {/* Complete — full-width emerald (matches routine "Mark done") */}
              <button
                type="button"
                onClick={() => { onMarkDone(event.id, 'task'); onClose() }}
                className="flex items-center justify-center gap-3 w-full min-h-[64px] rounded-2xl bg-emerald-500 text-white text-lg font-bold active:scale-[0.98] transition-transform"
              >
                <Check className="w-6 h-6" /> Mark complete
              </button>

              {/* Push presets — 2×2 grid of inset buttons */}
              <div className="grid grid-cols-2 gap-3">
                {PUSH_PRESETS.map(({ preset, label }) => {
                  const sub = targets[preset].sub
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => { onPushTask(event.id, preset); onClose() }}
                      className={`flex flex-col items-center justify-center w-full min-h-[64px] text-lg font-bold leading-tight active:scale-[0.98] transition-transform ${WALL.cardInset} ${WALL.inkStrong}`}
                    >
                      <span>{label}</span>
                      <span className={`text-sm font-semibold mt-0.5 ${WALL.muted}`}>{sub}</span>
                    </button>
                  )
                })}
              </div>
            </>
          ) : (
            <>
              {kind === 'routine' && (
                <button
                  type="button"
                  onClick={() => { onMarkDone(event.id, 'routine'); onClose() }}
                  className="flex items-center justify-center gap-3 w-full min-h-[64px] rounded-2xl bg-emerald-500 text-white text-lg font-bold active:scale-[0.98] transition-transform"
                >
                  <Check className="w-6 h-6" /> Mark done
                </button>
              )}

              <button
                type="button"
                onClick={() => { onSkip(event.id, kind); onClose() }}
                className={`flex items-center justify-center gap-3 w-full min-h-[64px] text-lg font-bold active:scale-[0.98] transition-transform ${WALL.cardInset} ${WALL.inkStrong}`}
              >
                <Redo2 className="w-6 h-6" /> Skip today
              </button>
            </>
          )}

          <button
            type="button"
            onClick={onClose}
            className={`flex items-center justify-center gap-2 w-full min-h-[56px] rounded-2xl text-base ${WALL.muted}`}
          >
            <X className="w-5 h-5" /> Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
